import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi as defaultAdminApi } from './admin-api';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { formatMoney, formatPaymentDate, PAYMENT_METHOD_LABELS } from '../lib/format';
import { PageState } from '../components/PageState';
import './admin.css';

const TITLE = 'Pagos';
const METHOD_OPTIONS = [
  { value: '', label: 'Todos los métodos' },
  ...Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label })),
];

// Only the keys the admin actually set travel to the server — an empty
// filter is simply omitted, letting `GET /payments` fall back to its own
// default (the current month, in Argentine time): the client never computes
// that window itself (Global Constraints: "el cliente nunca recalcula...
// fechas de período").
function buildParams(filters, extra) {
  const params = {};
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.method) params.method = filters.method;
  if (filters.memberId) params.memberId = filters.memberId;
  // Los anulados sólo viajan si se piden; los totales del servidor nunca los cuentan.
  if (filters.includeVoided) params.includeVoided = 1;
  return { ...params, ...extra };
}

const EMPTY_FILTERS = {
  from: '', to: '', method: '', memberId: '', includeVoided: false,
};

/**
 * `/admin/pagos` (HAMMER 1D E5, spec "Pagos (listado general)"): a read-only
 * view over `GET /payments`/`GET /payments/export` — payments themselves are
 * still only ever registered from a member's own detail page (Task 10).
 * Filters (date range/method/socio) are sent to the server as-is and drive a
 * full reload (never a local re-filter); pagination is the server's own
 * opaque `cursor`, fetched on demand via "Cargar más" and appended to what's
 * already on screen. Totals (`totals.count/amount/byMethod`) always describe
 * the WHOLE filtered set, not just the loaded page, so they never change
 * across a "Cargar más" click — only the row list grows.
 */
export function PaymentsPage({ api = defaultAdminApi }) {
  const { handleApiError } = useAuth();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [state, setState] = useState({
    status: 'loading', payments: [], totals: null, nextCursor: null, message: '',
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState('');
  const [members, setMembers] = useState([]);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [exportError, setExportError] = useState('');
  const requestVersion = useRef(0);

  // The socio filter's own options: fetched once, independent of the
  // payments list. A failure here only means the dropdown offers just "Todos
  // los socios" — the main `listPayments` call already reports/handles a
  // real session failure (a 401 hitting BOTH requests would otherwise try to
  // delegate to `handleApiError` twice for the same cause).
  useEffect(() => {
    let active = true;
    api.listMembers()
      .then((result) => { if (active) setMembers(Array.isArray(result) ? result : []); })
      .catch(() => { if (active) setMembers([]); });
    return () => { active = false; };
  }, [api]);

  const load = useCallback(() => {
    requestVersion.current += 1;
    let active = true;
    setLoadingMore(false);
    setState({
      status: 'loading', payments: [], totals: null, nextCursor: null, message: '',
    });
    setLoadMoreError('');
    api.listPayments(buildParams(filters))
      .then((result) => {
        if (!active) return;
        setState({
          status: 'ready',
          payments: (result && result.payments) || [],
          totals: (result && result.totals) || null,
          nextCursor: (result && result.nextCursor) || null,
          message: '',
        });
      })
      .catch((error) => {
        if (!active) return;
        const clearBusinessData = () => setState({
          status: 'loading', payments: [], totals: null, nextCursor: null, message: '',
        });
        const outcome = handleApiError(error, { clearBusinessData });
        if (outcome.handled) return;
        setState({
          status: 'error', payments: [], totals: null, nextCursor: null, message: errorMessageFor(error),
        });
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, filters, handleApiError]);

  useEffect(() => load(), [load]);

  function handleLoadMore() {
    if (!state.nextCursor || loadingMore) return;
    const version = requestVersion.current;
    setLoadMoreError('');
    setLoadingMore(true);
    api.listPayments(buildParams(filters, { cursor: state.nextCursor }))
      .then((result) => {
        if (version !== requestVersion.current) return;
        setState((prev) => ({
          ...prev,
          payments: [...prev.payments, ...((result && result.payments) || [])],
          nextCursor: (result && result.nextCursor) || null,
        }));
      })
      .catch((error) => {
        if (version !== requestVersion.current) return;
        const outcome = handleApiError(error);
        if (!outcome.handled) setLoadMoreError(errorMessageFor(error));
      })
      .finally(() => { if (version === requestVersion.current) setLoadingMore(false); });
  }

  async function handleExport(format) {
    setExportError('');
    setExportingFormat(format);
    try {
      await api.exportPayments(buildParams(filters, { format }));
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (!outcome.handled) setExportError(errorMessageFor(caught));
    } finally {
      setExportingFormat(null);
    }
  }

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-admin-payments__header">
        <h1 id="gym-page-title" className="gym-page__title">{TITLE}</h1>
      </header>

      {exportError && <p role="alert" className="gym-form__error">{exportError}</p>}

      <div className="gym-admin-payments__filters">
        <div className="gym-field">
          <label htmlFor="payments-from" className="gym-field__label">Desde</label>
          <input
            id="payments-from"
            type="date"
            className="gym-input"
            value={filters.from}
            onChange={(event) => setFilter('from', event.target.value)}
          />
        </div>
        <div className="gym-field">
          <label htmlFor="payments-to" className="gym-field__label">Hasta</label>
          <input
            id="payments-to"
            type="date"
            className="gym-input"
            value={filters.to}
            onChange={(event) => setFilter('to', event.target.value)}
          />
        </div>
        <div className="gym-field">
          <label htmlFor="payments-method" className="gym-field__label">Método</label>
          <select
            id="payments-method"
            className="gym-input"
            value={filters.method}
            onChange={(event) => setFilter('method', event.target.value)}
          >
            {METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="gym-field">
          <label htmlFor="payments-member" className="gym-field__label">Socio</label>
          <select
            id="payments-member"
            className="gym-input"
            value={filters.memberId}
            onChange={(event) => setFilter('memberId', event.target.value)}
          >
            <option value="">Todos los socios</option>
            {members.map((member) => <option key={member.id} value={member.id}>{member.username}</option>)}
          </select>
        </div>
      </div>
      <p className="gym-admin-field-hint">Si no elegís fechas, se muestra el mes actual.</p>
      <label className="gym-admin-checkbox gym-admin-payments__voided">
        <input type="checkbox" checked={filters.includeVoided} onChange={(event) => setFilter('includeVoided', event.target.checked)} />
        Incluir anulados
      </label>
      <div className="gym-admin-payments__export">
        <button
          type="button"
          className="gym-button gym-button--secondary"
          disabled={!!exportingFormat}
          onClick={() => handleExport('xlsx')}
        >
          {exportingFormat === 'xlsx' ? 'Exportando…' : 'Exportar Excel'}
        </button>
        <button
          type="button"
          className="gym-button gym-button--secondary"
          disabled={!!exportingFormat}
          onClick={() => handleExport('csv')}
        >
          {exportingFormat === 'csv' ? 'Exportando…' : 'Exportar CSV'}
        </button>
      </div>

      {state.status === 'loading' && (
        <div className="gym-state gym-state--loading" role="status" aria-live="polite">
          <span className="gym-state__spinner" aria-hidden="true" />
          <p className="gym-state__text">Cargando…</p>
        </div>
      )}

      {state.status === 'error' && (
        <PageState
          title={null}
          status="error"
          message={state.message}
          action={<button type="button" className="gym-button gym-button--primary" onClick={load}>Reintentar</button>}
        />
      )}

      {state.status === 'ready' && state.totals && (
        <div className="gym-metrics gym-admin-payments__totals">
          <div className="gym-metric-card gym-metric-card--accent">
            <p className="gym-metric-card__label">Total</p>
            <p className="gym-metric-card__value">{formatMoney(state.totals.amount)}</p>
          </div>
          <div className="gym-metric-card">
            <p className="gym-metric-card__label">Cantidad de pagos</p>
            <p className="gym-metric-card__value">{state.totals.count ?? '—'}</p>
          </div>
          <div className="gym-metric-card">
            <p className="gym-metric-card__label">Socios que pagaron</p>
            <p className="gym-metric-card__value">{state.totals.payersCount ?? '—'}</p>
          </div>
          <div className="gym-metric-card">
            <p className="gym-metric-card__label">Socios nuevos</p>
            <p className="gym-metric-card__value">{state.totals.newMembersCount ?? '—'}</p>
          </div>
          {METHOD_OPTIONS.filter((option) => option.value).map((option) => (
            <div key={option.value} className="gym-metric-card">
              <p className="gym-metric-card__label">{option.label}</p>
              <p className="gym-metric-card__value">{formatMoney(state.totals.byMethod[option.value])}</p>
            </div>
          ))}
        </div>
      )}

      {state.status === 'ready' && state.payments.length === 0 && (
        <div className="gym-state gym-state--empty">
          <p className="gym-state__text">No hay pagos que coincidan con estos filtros.</p>
        </div>
      )}

      {state.status === 'ready' && state.payments.length > 0 && (
        <>
          <table className="gym-table gym-admin-payments-table">
            <caption className="gym-sr-only">Listado de pagos</caption>
            <thead>
              <tr>
                <th scope="col">Fecha</th>
                <th scope="col">Socio</th>
                <th scope="col">Plan</th>
                <th scope="col">Método</th>
                <th scope="col">Importe</th>
              </tr>
            </thead>
            <tbody>
              {state.payments.map((payment) => (
                <tr key={payment.id}>
                  <td data-label="Fecha">{formatPaymentDate(payment)}</td>
                  <td data-label="Socio">{payment.memberName || <span className="gym-muted-text">—</span>}</td>
                  <td data-label="Plan">{payment.planName || <span className="gym-muted-text">—</span>}</td>
                  <td data-label="Método">{PAYMENT_METHOD_LABELS[payment.method] || payment.method}</td>
                  <td data-label="Importe">
                    {formatMoney(payment.amount)}
                    {payment.status === 'voided' && (
                      <span className="gym-admin-payments__void">
                        <span className="gym-badge gym-badge--neutral">Anulado</span>
                        {payment.voidReason && <span className="gym-muted-text">Motivo: {payment.voidReason}</span>}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {loadMoreError && <p role="alert" className="gym-form__error">{loadMoreError}</p>}

          {state.nextCursor && (
            <div className="gym-admin-payments__more">
              <button
                type="button"
                className="gym-button gym-button--secondary"
                disabled={loadingMore}
                onClick={handleLoadMore}
              >
                {loadingMore ? 'Cargando…' : 'Cargar más'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
